import {
  type AnyEvent,
  type ChildState,
  DEFAULT_FLAGS,
  type EventType,
  type Flags,
  fold,
  localDateOf,
  type PayloadOf,
} from '@disdis/shared'
import { create } from 'zustand'
import { log, reason } from '../app/log.js'
import { catalog } from './content.js'
import { getMeta, setMeta } from './db.js'
import { activeChildId, setActiveChildId } from './device.js'
import { appendEvent, loadEvents } from './events.js'
import type { FoldRequest, FoldResponse } from './fold.worker.js'
import { scheduleSync } from './sync.js'

const logger = log('store')

export type StateStatus = 'idle' | 'loading' | 'ready' | 'error'

type ChildStore = {
  childId: string | null
  status: StateStatus
  error: string | null
  state: ChildState | null
  flags: Flags
  /** Reads meta, loads the log, folds it. Safe to call again at any time. */
  boot: () => Promise<void>
  selectChild: (childId: string | null) => Promise<void>
  refresh: () => Promise<void>
  append: <T extends EventType>(type: T, payload: PayloadOf<T>) => Promise<AnyEvent | null>
  setFlags: (flags: Flags) => void
}

let worker: Worker | null = null
let requestId = 0
const pending = new Map<number, (response: FoldResponse) => void>()

function foldWorker(): Worker | null {
  if (typeof Worker === 'undefined') return null
  if (worker) return worker
  worker = new Worker(new URL('./fold.worker.ts', import.meta.url), { type: 'module' })
  worker.onmessage = (message: MessageEvent<FoldResponse>) => {
    const resolve = pending.get(message.data.id)
    if (!resolve) return
    pending.delete(message.data.id)
    resolve(message.data)
  }
  worker.onerror = (event) => {
    logger.error('the fold worker failed, falling back to the main thread', event.message)
    worker?.terminate()
    worker = null
    for (const [id, resolve] of pending) {
      resolve({ id, ok: false, error: event.message })
    }
    pending.clear()
  }
  return worker
}

/** Folds in the worker when there is one, on the main thread in tests and old browsers. */
export async function foldEvents(events: AnyEvent[], today: string): Promise<ChildState> {
  const instance = foldWorker()
  if (!instance) return fold(events, { today, catalog })
  const id = ++requestId
  const response = await new Promise<FoldResponse>((resolve) => {
    pending.set(id, resolve)
    const request: FoldRequest = { id, events, today }
    instance.postMessage(request)
  })
  if (!response.ok) {
    logger.warn('the worker could not fold, folding on the main thread', response.error)
    return fold(events, { today, catalog })
  }
  for (const issue of response.issues) logger.warn('fold skipped a row', issue)
  return response.state
}

export const useChildStore = create<ChildStore>((set, get) => ({
  childId: null,
  status: 'idle',
  error: null,
  state: null,
  flags: DEFAULT_FLAGS,

  boot: async () => {
    set({ status: 'loading', error: null })
    try {
      const [childId, storedFlags] = await Promise.all([
        activeChildId(),
        getMeta<Flags>('featureFlags'),
      ])
      set({ childId, flags: storedFlags ?? DEFAULT_FLAGS })
      if (!childId) {
        set({ status: 'ready', state: null })
        return
      }
      await get().refresh()
    } catch (error) {
      logger.error('boot failed', reason(error))
      set({ status: 'error', error: reason(error) })
    }
  },

  selectChild: async (childId) => {
    await setActiveChildId(childId)
    set({ childId, state: null })
    if (childId) await get().refresh()
    else set({ status: 'ready' })
  },

  refresh: async () => {
    const childId = get().childId
    if (!childId) return
    set({ status: 'loading', error: null })
    try {
      const events = await loadEvents(childId)
      const state = await foldEvents(events, localDateOf())
      set({ state, status: 'ready', error: null })
    } catch (error) {
      logger.error('could not fold the child state', reason(error))
      set({ status: 'error', error: reason(error) })
    }
  },

  append: async (type, payload) => {
    const childId = get().childId
    if (!childId) {
      logger.warn(`refused to append ${type} without an active child`)
      return null
    }
    const event = await appendEvent(type, payload, {
      childId,
      reviewMode: get().state?.profile.reviewMode ?? false,
    })
    if (!event) return null
    // ARCHITECTURE §5: every child append reaches the API after the 2 s debounce. The parent
    // side does the same in appendParentEvent; without this call a ritual never left the phone.
    scheduleSync(childId)
    await get().refresh()
    return event
  },

  setFlags: (flags) => {
    set({ flags })
    void setMeta('featureFlags', flags).catch((error) =>
      logger.error('could not cache the feature flags', reason(error)),
    )
  },
}))
